import {
  isPrivateAttributeKey,
  privateAttributes,
} from "@/cart/cartAttributes";
import type { AddItemContext, CartStateGetSet } from "@/cart/types";
import GRAPHQL_CLIENT from "@/graphql/client";
import type { Cart, CartLineInput } from "@/graphql/storefront/generated";
import {
  MATIC_BAG_PASS_VARIANT_ID,
  MATIC_REFERRAL_BAG_PASS_VARIANT_ID,
  MATIC_REFKIT_VARIANT_ID,
} from "@/consts/shopify";
import posthog from "posthog-js";
import { robotItemsAdded, trackOpenAI } from "@/analytics/openai";
import { trackMetaAddToCart } from "@/analytics/meta";
import { trackPinterest } from "@/analytics/pinterest";
import { metaAddToCartData } from "@/lib/metaConversions";
import { getReferralCode } from "@/cart/maticReferralReward";
import { lookUpReferralReward } from "@/cart/referralReward";
import { isMaticFamilyVariantId } from "@/utils/maticFamily";

const hasNonEmptyAttributes = (
  attributes: Array<{ key: string; value?: string | null }> | undefined,
) => {
  return (attributes || []).some(
    (attribute) =>
      Boolean(attribute.value) && !isPrivateAttributeKey(attribute.key),
  );
};

export const addItem = ({ get, set }: CartStateGetSet) => {
  return async (
    variantId: string,
    context?: AddItemContext,
    quantity?: number,
  ) => {
    let lines: CartLineInput[] = [
      {
        merchandiseId: variantId,
        quantity: quantity ?? 1,
        attributes: [],
      },
    ];
    if (
      variantId === MATIC_REFKIT_VARIANT_ID ||
      variantId === MATIC_REFERRAL_BAG_PASS_VARIANT_ID
    ) {
      return;
    }

    const isMaticVar = isMaticFamilyVariantId(variantId);

    let cart: Cart | undefined;

    const { cartId, cart: currentCart } = get();

    // A code that gives the referral-edition pass gets it through the reward
    // sync, so the standard pass is never added alongside it.
    const referralCode = getReferralCode(get, currentCart);
    if (
      variantId === MATIC_BAG_PASS_VARIANT_ID &&
      referralCode &&
      (await lookUpReferralReward(referralCode)) === "referral-bag-pass"
    ) {
      return;
    }

    if (isMaticVar && cartId && currentCart) {
      const existingMaticLine = currentCart.lines.edges.find(
        ({ node }) => node.merchandise.id === variantId,
      );
      const hasGiftLikeAttributes =
        hasNonEmptyAttributes(existingMaticLine?.node.attributes) ||
        hasNonEmptyAttributes(currentCart.attributes);

      if (existingMaticLine && hasGiftLikeAttributes) {
        await GRAPHQL_CLIENT.UpdateCartAndLineAttributes({
          cartId,
          cartAttributes: privateAttributes(currentCart.attributes),
          lines: [
            {
              id: existingMaticLine.node.id,
              attributes: privateAttributes(existingMaticLine.node.attributes),
            },
          ],
        });

        const { cartLinesUpdate } = await GRAPHQL_CLIENT.UpdateCartLine({
          cartId,
          lines: [
            {
              id: existingMaticLine.node.id,
              quantity: existingMaticLine.node.quantity + 1,
            },
          ],
        });
        cart = cartLinesUpdate?.cart as Cart;
      } else {
        cart = await addToOrCreateCart(lines, cartId);
      }
    } else {
      cart = await addToOrCreateCart(lines, cartId);
    }

    if (!cart) throw new Error("addItem: Adding line to cart failed");

    posthog.capture("add_item", {
      variantId,
      cartId: cart.id,
      source: context?.source,
      button_location: context?.buttonLocation,
    });

    trackMetaAddToCart(() => {
      const addedLine = cart.lines.edges.find(
        ({ node }) => node.merchandise.id === variantId,
      )?.node;
      return addedLine === undefined
        ? null
        : metaAddToCartData(addedLine, quantity ?? 1);
    });

    trackPinterest("addtocart", () => {
      const addedLine = cart.lines.edges.find(
        ({ node }) => node.merchandise.id === variantId,
      )?.node;
      if (addedLine === undefined) return null;
      const addedQuantity = quantity ?? 1;
      return {
        value: Number(addedLine.merchandise.price.amount) * addedQuantity,
        order_quantity: addedQuantity,
        currency: addedLine.merchandise.price.currencyCode,
        line_items: [
          {
            product_id: addedLine.merchandise.product.id.split("/").pop() ?? "",
            product_quantity: addedQuantity,
          },
        ],
      };
    });

    trackOpenAI("items_added", () => {
      const addedLine = cart.lines.edges.find(
        ({ node }) => node.merchandise.id === variantId,
      )?.node;
      if (addedLine === undefined) return null;
      return robotItemsAdded([{ line: addedLine, quantity: quantity ?? 1 }]);
    });

    set({ cartId: cart.id, cart });
  };
};

export const addToOrCreateCart = async (
  lines: CartLineInput[],
  cartId: string | undefined,
  /** The full attribute set the cart should carry once the lines are on it. */
  cartAttributes?: Record<string, string>,
) => {
  const attributes =
    cartAttributes === undefined
      ? undefined
      : Object.entries(cartAttributes).map(([key, value]) => ({ key, value }));

  if (cartId) {
    // Stamped before the lines land, so the cart the add returns already
    // carries whatever discount the stamp unlocks.
    if (attributes !== undefined) {
      const { updateCartAttributes } =
        await GRAPHQL_CLIENT.UpdateCartAndLineAttributes({
          cartId,
          cartAttributes: attributes,
          lines: [],
        });
      const errors = updateCartAttributes?.userErrors ?? [];
      if (errors.length > 0) {
        throw new Error(
          `addToOrCreateCart: stamping the cart failed: ${errors.map((error) => error.message).join(", ")}`,
        );
      }
    }
    const { cartLinesAdd } = await GRAPHQL_CLIENT.AddToCart({
      cartId,
      lines,
    });
    return cartLinesAdd?.cart as Cart;
  } else {
    posthog.capture("create_cart", { lines, cartId });
    const { cartCreate } = await GRAPHQL_CLIENT.CartCreate({
      input: attributes === undefined ? { lines } : { lines, attributes },
    });
    return cartCreate?.cart as Cart;
  }
};
